import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  CreditCard,
  Search,
  Eye,
  RefreshCw,
  FileSpreadsheet,
  ChevronDown,
  ChevronUp,
  Filter,
  X,
  User,
  Building2,
  Store,
  ShieldCheck,
  Copy,
  AlertTriangle,
  Gamepad2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { LoadingSpinner } from '../../ui/Loading';
import ExportConfirmModal from '../../ui/ExportConfirmModal';
import GatewayBadge from '../../ui/GatewayBadge';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/Table';
import { useTransactions, useUserDetails } from '../../../hooks/useApi';
import { useAuth } from '../../../hooks/useAuth';
import { Transaction, TransactionAllocation, TransactionFilters } from '../../../types';
import { PROVIDER_FILTER_OPTIONS, PROVIDER_CONFIG } from '../../../constants/provider';
import { format } from 'date-fns';
import { NumberedPagination, SortableTableHead } from '@kioskgaming/ui';
import useTableSort from '../../../hooks/useTableSort';
import { getTableSortColumns } from '../../../config/tableSortConfig';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { exportToExcel } from '../../../utils';
import { cn } from '../../../utils';
import { apiClient } from '../../../services/api';
import { toast } from 'react-hot-toast';
import {
  getAllocationChildStatusDisplay,
  getOverallStatusDisplay,
  getTransactionListAmountColumns,
  getTransactionListStatusDisplay,
  formatCashAmount,
  formatCreditsAmount,
} from '../../../utils/transactionDisplayUtils';
import {
  applyWithdrawalPayoutRefundListAmount,
  getAdminTransactionStatusDisplay,
  getWithdrawalPayoutRefundInfo,
} from '../../../utils/withdrawalPayoutRefundDisplay';
import {
  AdminTableColgroup,
  ADMIN_TABLE_FIXED_CLASS,
  adminCellClass,
  PAYMENT_TRANSACTION_COLUMNS,
  PT,
} from '../../../config/adminTableLayout';

interface TransactionListProps {
  filters: TransactionFilters;
  setFilters: React.Dispatch<React.SetStateAction<TransactionFilters>>;
  onViewTransaction: (transaction: Transaction) => void;
}

const getStatusBadgeCompact = (status: string | null | undefined) => {
  const display = getOverallStatusDisplay(status);
  return getStatusBadgeFromDisplay(display);
};

function getStatusBadgeFromDisplay(display: {
  label: string;
  className: string;
}) {
  return (
    <span
      className={cn(
        'inline-block rounded px-1 py-0.5 text-[10px] font-medium whitespace-nowrap leading-tight',
        display.className,
      )}
    >
      {display.label}
    </span>
  );
}

const getAllocationStatusBadgeCompact = (status: string | null | undefined) => {
  const display = getAllocationChildStatusDisplay(status);
  return (
    <span
      className={cn(
        'inline-block rounded px-1 py-0.5 text-[10px] font-medium whitespace-nowrap leading-tight',
        display.className,
      )}
    >
      {display.label}
    </span>
  );
};

function amountToneClass(tone: 'positive' | 'negative' | 'neutral'): string {
  if (tone === 'positive') return 'text-green-600';
  if (tone === 'negative') return 'text-red-600';
  return 'text-muted-foreground';
}

function ListAmountCell({
  label,
  tone,
  direction,
  unit,
  subLabel,
}: {
  label: string | null;
  tone: 'positive' | 'negative' | 'neutral';
  direction?: 'in' | 'out' | null;
  unit?: string | null;
  subLabel?: string | null;
}) {
  if (!label) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  const sign = direction === 'in' ? '+' : direction === 'out' ? '−' : '';

  return (
    <div className="inline-flex flex-col items-end gap-0.5">
      <span className={cn('font-semibold tabular-nums text-xs whitespace-nowrap', amountToneClass(tone))}>
        {sign}
        {label}
        {unit ? ` ${unit.toUpperCase()}` : null}
      </span>
      {subLabel ? (
        <span className="text-[10px] font-medium tracking-wide text-muted-foreground text-right leading-tight">
          {subLabel}
        </span>
      ) : null}
    </div>
  );
}

const paymentMethodShort = (paymentMethod: string) => {
  const key = (paymentMethod || '').toLowerCase().replace(/\s+/g, '_');
  const map: Record<string, string> = {
    bank_transfer: 'Bank',
    bitcoin_transfer: 'BTC',
    bitcoin: 'BTC',
    btc_onchain: 'BTC',
    cashapp: 'Cash App',
    cashapp_transfer: 'Cash App',
    chime: 'Chime',
    chime_transfer: 'Chime',
    applepay: 'Apple Pay',
    googlepay: 'Google Pay',
    ggus_btcpay: 'BTC LN',
    bank: 'Bank',
  };
  if (map[key]) return map[key];
  if (!paymentMethod) return '—';
  return paymentMethod.length > 14 ? `${paymentMethod.slice(0, 12)}…` : paymentMethod;
};

function allocationCountHint(transaction: Transaction): number | null {
  if (transaction.childAllocationCount && transaction.childAllocationCount > 0) {
    return transaction.childAllocationCount;
  }
  const metaAlloc = transaction.metadata?.allocations;
  const syncAlloc = (transaction.syncMetadata as { allocations?: unknown[] } | undefined)?.allocations;
  const count = Array.isArray(metaAlloc)
    ? metaAlloc.length
    : Array.isArray(syncAlloc)
      ? syncAlloc.length
      : 0;
  return count > 0 ? count : null;
}

interface TransactionTableRowProps {
  transaction: Transaction;
  onViewTransaction: (transaction: Transaction) => void;
  hasAllocations: boolean;
  isExpanded: boolean;
  isLoadingAllocations: boolean;
  onToggleExpand: (event: React.MouseEvent) => void;
}

interface TransactionAllocationRowProps {
  allocation: TransactionAllocation;
  colSpan: number;
}

const TransactionAllocationRow: React.FC<TransactionAllocationRowProps> = ({ allocation, colSpan }) => {
  const fmt = useFormatDate();
  const gameLabel =
    allocation.gameProviderName ||
    allocation.gameProviderCode ||
    allocation.gameProviderId?.slice(0, 8) ||
    'Game allocation';
  const amount = Math.abs(Number(allocation.amount || 0));
  const displayStatus = allocation.displayStatus || allocation.status;

  return (
    <TableRow className="bg-muted/30 hover:bg-muted/40 border-b border-muted/60">
      <TableCell colSpan={colSpan} className="px-2 py-2 align-top">
        <div className="flex flex-col gap-1 pl-7 border-l-2 border-violet-300/70 ml-2">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-medium text-foreground">{gameLabel}</span>
            {getAllocationStatusBadgeCompact(displayStatus)}
            <span className="font-semibold tabular-nums text-green-600">
              +{formatCreditsAmount(amount)} credits
            </span>
            <span className="font-mono text-[10px] text-muted-foreground truncate max-w-[12rem]" title={allocation.transactionId}>
              #{allocation.id} · {allocation.transactionId}
            </span>
          </div>
          {allocation.description ? (
            <div className="text-[11px] text-muted-foreground line-clamp-1" title={allocation.description}>
              {allocation.description}
            </div>
          ) : null}
          {allocation.errorMessage &&
          (displayStatus === 'failed' || displayStatus === 'rejected' || displayStatus === 'refunded') ? (
            <div className="text-[11px] text-red-600 line-clamp-2" title={allocation.errorMessage}>
              {allocation.errorMessage}
            </div>
          ) : null}
          {allocation.refundTransactionId ? (
            <div className="text-[10px] text-slate-600">Refund: {allocation.refundTransactionId}</div>
          ) : null}
          <div className="text-[10px] text-muted-foreground tabular-nums">
            {fmt(allocation.created_at, { second: undefined })}
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
};

const TransactionTableRow: React.FC<TransactionTableRowProps> = React.memo(
  ({ transaction, onViewTransaction, hasAllocations, isExpanded, isLoadingAllocations, onToggleExpand }) => {
    const fmt = useFormatDate();
    const buyerType = transaction.buyerType;
    const buyerId = transaction.buyerId;
    const shopAgentId = (transaction.metadata as { agentId?: string } | undefined)?.agentId;
    const isPlayerLike = !buyerType || buyerType === 'user';
    const hasEmbeddedContact = Boolean(transaction.user?.phone || transaction.user?.email);
    const needsUserFetch = Boolean(transaction.userId) && isPlayerLike && !hasEmbeddedContact;

    const { data: userData, isLoading: userLoading } = useUserDetails(transaction.userId || '', {
      enabled: needsUserFetch,
    });
    const fetchedUser = userData?.data?.user;

    const handleRowClick = useCallback(() => {
      onViewTransaction(transaction);
    }, [onViewTransaction, transaction]);

    const copyTxnId = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        void navigator.clipboard.writeText(transaction.transactionId);
        toast.success('Transaction ID copied');
      },
      [transaction.transactionId]
    );

    const recipient = transaction.recipient;
    const isOrgBuyer =
      buyerType === 'agent' || buyerType === 'super_agent' || buyerType === 'shop';
    const recipientHref =
      recipient?.refLink ||
      (buyerType === 'agent' && buyerId
        ? `/agents/${buyerId}`
        : buyerType === 'super_agent' && buyerId
          ? `/super-agents/${buyerId}`
          : buyerType === 'shop' && buyerId
            ? `/shops/${buyerId}`
            : null);
    const recipientLabel = recipient?.displayLabel || recipient?.name || null;

    const buyerContent = (() => {
      if (recipientLabel && buyerId && isOrgBuyer) {
        if (recipientHref) {
          return (
            <Link
              to={recipientHref}
              className="text-primary hover:underline max-w-[11rem] truncate block font-medium"
              onClick={(e) => e.stopPropagation()}
              title={recipientLabel}
            >
              {recipientLabel}
            </Link>
          );
        }
        return (
          <span className="truncate max-w-[11rem] block font-medium" title={recipientLabel}>
            {recipientLabel}
          </span>
        );
      }
      if (buyerType === 'agent' && buyerId) {
        return (
          <Link
            to={`/agents/${buyerId}`}
            className="text-primary hover:underline max-w-[10rem] truncate block"
            onClick={(e) => e.stopPropagation()}
            title={buyerId}
          >
            Agent · {buyerId.slice(0, 8)}…
          </Link>
        );
      }
      if (buyerType === 'super_agent' && buyerId) {
        return (
          <Link
            to={`/super-agents/${buyerId}`}
            className="text-primary hover:underline max-w-[10rem] truncate block"
            onClick={(e) => e.stopPropagation()}
            title={buyerId}
          >
            Super agent · {buyerId.slice(0, 8)}…
          </Link>
        );
      }
      if (buyerType === 'shop' && buyerId) {
        if (shopAgentId || recipientHref) {
          return (
            <Link
              to={`/shops/${buyerId}`}
              className="text-primary hover:underline max-w-[10rem] truncate block"
              onClick={(e) => e.stopPropagation()}
            >
              Shop · {buyerId.slice(0, 8)}…
            </Link>
          );
        }
        return <span className="text-muted-foreground truncate max-w-[10rem] block">Shop · {buyerId.slice(0, 8)}…</span>;
      }
      const phone = transaction.user?.phone;
      const email = transaction.user?.email;
      if (phone) {
        return transaction.userId ? (
          <Link
            to={`/users/${transaction.userId}`}
            className="text-foreground hover:underline font-medium truncate max-w-[11rem] block"
            onClick={(e) => e.stopPropagation()}
          >
            {phone}
          </Link>
        ) : (
          <span className="font-medium truncate max-w-[11rem] block">{phone}</span>
        );
      }
      if (email) {
        return transaction.userId ? (
          <Link
            to={`/users/${transaction.userId}`}
            className="text-foreground hover:underline truncate max-w-[11rem] block text-xs"
            onClick={(e) => e.stopPropagation()}
          >
            {email}
          </Link>
        ) : (
          <span className="truncate max-w-[11rem] block text-xs">{email}</span>
        );
      }
      if (userLoading) {
        return <span className="text-muted-foreground text-xs italic">…</span>;
      }
      if (fetchedUser?.phone && transaction.userId) {
        return (
          <Link
            to={`/users/${transaction.userId}`}
            className="text-foreground hover:underline font-medium truncate max-w-[11rem] block"
            onClick={(e) => e.stopPropagation()}
          >
            {fetchedUser.phone}
          </Link>
        );
      }
      if (fetchedUser?.email && transaction.userId) {
        return (
          <Link
            to={`/users/${transaction.userId}`}
            className="text-foreground hover:underline truncate max-w-[11rem] block text-xs"
            onClick={(e) => e.stopPropagation()}
          >
            {fetchedUser.email}
          </Link>
        );
      }
      return <span className="text-muted-foreground">—</span>;
    })();

    const desc = (transaction.description || '').trim();
    const rowTitle = [transaction.transactionId, desc || undefined].filter(Boolean).join(' · ');
    const amountCols = applyWithdrawalPayoutRefundListAmount(
      getTransactionListAmountColumns(transaction),
      transaction,
      transaction.currency || 'USD',
    );
    const listStatus = getAdminTransactionStatusDisplay(transaction);
    const payoutRefundInfo = getWithdrawalPayoutRefundInfo(transaction);
    const allocationHint = allocationCountHint(transaction);

    return (
      <TableRow
        className="cursor-pointer"
        onClick={handleRowClick}
        title={rowTitle}
      >
        <TableCell
          className={cn(
            'px-2 py-2 align-top text-xs font-mono text-muted-foreground min-w-0',
            adminCellClass(PAYMENT_TRANSACTION_COLUMNS[PT.id]),
          )}
          title={`Internal ID: ${transaction.id}`}
        >
          {transaction.id}
        </TableCell>
        <TableCell
          className={cn('px-2 py-2 align-top min-w-0', adminCellClass(PAYMENT_TRANSACTION_COLUMNS[PT.buyer]))}
        >
          {buyerContent}
        </TableCell>
        <TableCell
          className={cn('px-1 py-2 align-top min-w-0', adminCellClass(PAYMENT_TRANSACTION_COLUMNS[PT.status]))}
        >
          <div className="flex flex-col gap-0.5">
            {getStatusBadgeFromDisplay(listStatus)}
            {transaction.fulfillmentStatus &&
            transaction.fulfillmentStatus !== transaction.status ? (
              <span className="text-[9px] text-muted-foreground leading-tight" title={`Payment status: ${transaction.status}`}>
                Pay: {transaction.status}
              </span>
            ) : null}
            {transaction.fraudFlagged || transaction.flaggedInfo?.isFlagged ? (
              <span
                className="inline-flex w-fit items-center rounded-full bg-amber-100 px-1 py-0 text-[9px] font-medium text-amber-900"
                title={transaction.fraudFlag?.ruleOrReason || 'Flagged for review'}
              >
                <AlertTriangle className="mr-0.5 h-2.5 w-2.5" />
                Flag
              </span>
            ) : null}
          </div>
        </TableCell>
        <TableCell
          className={cn(
            'px-2 py-2 align-top text-right whitespace-nowrap min-w-0',
            adminCellClass(PAYMENT_TRANSACTION_COLUMNS[PT.usdAmount]),
          )}
        >
          <ListAmountCell
            label={amountCols.usdLabel}
            tone={amountCols.usdTone}
            direction={amountCols.usdDirection}
            unit={amountCols.usdUnit}
            subLabel={amountCols.usdSubLabel}
          />
        </TableCell>
        <TableCell
          className={cn(
            'px-2 py-2 align-top text-right whitespace-nowrap min-w-0',
            adminCellClass(PAYMENT_TRANSACTION_COLUMNS[PT.creditAmount]),
          )}
        >
          <ListAmountCell
            label={amountCols.creditLabel}
            tone={amountCols.creditTone}
            direction={amountCols.creditDirection}
            unit={amountCols.creditUnit}
          />
        </TableCell>
        <TableCell
          className={cn('px-2 py-2 align-top min-w-0', adminCellClass(PAYMENT_TRANSACTION_COLUMNS[PT.gateway]))}
        >
          <GatewayBadge provider={transaction.provider} className="!px-1.5 !py-0 !text-[10px] max-w-full truncate block" />
        </TableCell>
        <TableCell
          className={cn(
            'px-2 py-2 align-top text-xs text-muted-foreground min-w-0 truncate',
            adminCellClass(PAYMENT_TRANSACTION_COLUMNS[PT.method]),
          )}
          title={transaction.paymentMethod}
        >
          {paymentMethodShort(transaction.paymentMethod)}
        </TableCell>
        <TableCell
          className={cn('px-2 py-2 align-top min-w-0', adminCellClass(PAYMENT_TRANSACTION_COLUMNS[PT.txnId]))}
        >
          <div className="flex items-center gap-1">
            <span className="font-mono text-[11px] text-muted-foreground truncate" title={transaction.transactionId}>
              {transaction.transactionId.length > 18
                ? `${transaction.transactionId.slice(0, 10)}…${transaction.transactionId.slice(-6)}`
                : transaction.transactionId}
            </span>
            <button
              type="button"
              className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={copyTxnId}
              aria-label="Copy transaction ID"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>
          {desc ? (
            <div className="mt-0.5 line-clamp-1 text-[11px] text-muted-foreground" title={desc}>
              {desc}
            </div>
          ) : null}
          {payoutRefundInfo ? (
            <div
              className="mt-0.5 line-clamp-2 text-[11px] font-medium text-emerald-700"
              title="Player wallet was credited back after payout failure"
            >
              Refunded{' '}
              {formatCashAmount(payoutRefundInfo.refundAmount, transaction.currency || 'USD')} to player wallet
            </div>
          ) : null}
          {transaction.errorMessage &&
          (listStatus.status === 'failed' ||
            listStatus.status === 'failed_refunded' ||
            listStatus.status === 'rejected' ||
            listStatus.status === 'cancelled' ||
            transaction.status === 'failed' ||
            transaction.status === 'rejected' ||
            transaction.status === 'cancelled') ? (
            <div
              className={cn(
                'mt-0.5 line-clamp-2 text-[11px] text-red-600',
                payoutRefundInfo ? 'text-red-500/90' : undefined,
              )}
              title={transaction.errorMessage}
            >
              {payoutRefundInfo ? `Payout error: ${transaction.errorMessage}` : transaction.errorMessage}
            </div>
          ) : null}
          {hasAllocations ? (
            <button
              type="button"
              className={cn(
                'mt-1.5 inline-flex w-full max-w-full items-center justify-between gap-1.5 rounded-md border px-2 py-1 text-left text-[10px] font-medium transition-colors',
                isExpanded
                  ? 'border-violet-400 bg-violet-100 text-violet-950'
                  : 'border-violet-300 bg-violet-50 text-violet-900 hover:bg-violet-100',
              )}
              onClick={onToggleExpand}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'Hide game allocation rows' : 'Show game allocation rows'}
            >
              <span className="inline-flex min-w-0 items-center gap-1">
                <Gamepad2 className="h-3 w-3 shrink-0" />
                <span className="truncate">
                  {allocationHint != null
                    ? `${allocationHint} game allocation${allocationHint === 1 ? '' : 's'}`
                    : 'Multi-game deposit'}
                </span>
              </span>
              <span className="inline-flex shrink-0 items-center gap-0.5 text-[9px] uppercase tracking-wide">
                {isLoadingAllocations ? (
                  <span className="inline-block h-3 w-3 animate-pulse rounded-full bg-violet-400/50" />
                ) : (
                  <>
                    {isExpanded ? 'Hide' : 'Show'}
                    {isExpanded ? (
                      <ChevronUp className="h-3 w-3" />
                    ) : (
                      <ChevronDown className="h-3 w-3" />
                    )}
                  </>
                )}
              </span>
            </button>
          ) : null}
        </TableCell>
        <TableCell
          className={cn(
            'px-2 py-2 align-top whitespace-nowrap text-xs text-muted-foreground min-w-0',
            adminCellClass(PAYMENT_TRANSACTION_COLUMNS[PT.created]),
          )}
        >
          <div>{fmt(transaction.created_at, { second: undefined })}</div>
          <div className="text-[11px] opacity-80">{fmt(transaction.created_at, { year: undefined, month: undefined, day: undefined, second: undefined })}</div>
        </TableCell>
        <TableCell className={cn('px-2 py-2 align-top', adminCellClass(PAYMENT_TRANSACTION_COLUMNS[PT.actions]))}>
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); handleRowClick(); }} aria-label="View details">
              <Eye className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  }
);
TransactionTableRow.displayName = 'TransactionTableRow';

const DEFAULT_PAGINATION = {
  page: 1,
  limit: 20,
  total: 0,
  pages: 1
};

const EXPORT_LIMIT = 1000;

const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;

const ALLOCATION_PRELOAD_CONCURRENCY = 6;

/** Sent to GET /admin/transactions — server sorts then paginates. */
type TxSortColumn = 'status' | 'amount' | 'creditAmount' | 'created_at';
type TxSortOrder = 'ASC' | 'DESC';

const TransactionList: React.FC<TransactionListProps> = ({
  filters,
  setFilters,
  onViewTransaction
}) => {
  const fmt = useFormatDate();
  const { user: currentAdmin } = useAuth();
  // KIOSK-720: Export Data = No for support_admin (permission matrix).
  const canExport = currentAdmin?.role !== 'support_admin';
  const tableSort = useTableSort({
    columns: getTableSortColumns('paymentTransactions'),
    onChange: () => setFilters((prev) => ({ ...prev, page: 1 })),
  });
  const { data: transactionsData, isLoading, error, refetch } = useTransactions(filters);
  const sortBy: TxSortColumn =
    filters.sortBy === 'status' ||
    filters.sortBy === 'amount' ||
    filters.sortBy === 'creditAmount' ||
    filters.sortBy === 'created_at'
      ? filters.sortBy
      : 'created_at';
  const sortOrder: TxSortOrder = filters.sortOrder === 'ASC' ? 'ASC' : 'DESC';

  useEffect(() => {
    setFilters((prev) => {
      const nextSortBy = tableSort.sortParams.sortBy;
      const nextSortOrder = tableSort.sortParams.sortOrder;
      if (prev.sortBy === nextSortBy && prev.sortOrder === nextSortOrder) {
        return prev;
      }
      const next: TransactionFilters = { ...prev, page: 1 };
      if (nextSortBy && nextSortOrder) {
        next.sortBy = nextSortBy as TransactionFilters['sortBy'];
        next.sortOrder = nextSortOrder;
      } else {
        delete next.sortBy;
        delete next.sortOrder;
      }
      return next;
    });
  }, [tableSort.sortBy, tableSort.sortOrder, setFilters]);

  const appliedSort = useMemo(
    () => ({
      sortBy: transactionsData?.data?.pagination?.sortBy ?? null,
      sortOrder: transactionsData?.data?.pagination?.sortOrder ?? null,
    }),
    [transactionsData?.data?.pagination?.sortBy, transactionsData?.data?.pagination?.sortOrder],
  );

  const headerSort = tableSort.sortBy
    ? {
        activeKey: appliedSort.sortBy ?? tableSort.sortBy,
        direction: appliedSort.sortOrder ?? tableSort.sortOrder,
      }
    : { activeKey: null, direction: null };

  const [searchValue, setSearchValue] = useState(filters.search || '');
  const [emailValue, setEmailValue] = useState(filters.email || '');
  const [phoneValue, setPhoneValue] = useState(filters.phone || '');
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(() => Boolean(filters.email || filters.phone));
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipEmailDebounceRef = useRef(false);
  const skipPhoneDebounceRef = useRef(false);
  const [expandedParentIds, setExpandedParentIds] = useState<Set<number>>(() => new Set());
  const [allocationsByParentId, setAllocationsByParentId] = useState<Record<number, TransactionAllocation[]>>({});
  const [loadingAllocationIds, setLoadingAllocationIds] = useState<Set<number>>(() => new Set());
  const [failedAllocationIds, setFailedAllocationIds] = useState<Set<number>>(() => new Set());
  const preloadRunRef = useRef(0);

  const TABLE_COL_COUNT = 10;

  const isPlayerTab = (filters.buyerScope || 'user') === 'user';

  useEffect(() => {
    if (filters.email || filters.phone) {
      setMoreFiltersOpen(true);
    }
  }, [filters.email, filters.phone]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setSearchValue(filters.search || '');
  }, [filters.search]);

  useEffect(() => {
    const newValue = filters.email || '';
    if (newValue !== emailValue) {
      skipEmailDebounceRef.current = true;
      setEmailValue(newValue);
    }
  }, [filters.email]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const newValue = filters.phone || '';
    if (newValue !== phoneValue) {
      skipPhoneDebounceRef.current = true;
      setPhoneValue(newValue);
    }
  }, [filters.phone]); // eslint-disable-line react-hooks/exhaustive-deps

  const applySearchFilter = useCallback(
    (raw: string) => {
      const nextSearch = raw.trim() || undefined;
      if (nextSearch === (filters.search || undefined)) return;
      setFilters((prev) => ({ ...prev, search: nextSearch, page: 1 }));
    },
    [filters.search, setFilters],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => applySearchFilter(value), 300);
    },
    [applySearchFilter],
  );

  const handleClearSearch = useCallback(() => {
    setSearchValue('');
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    applySearchFilter('');
  }, [applySearchFilter]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (skipEmailDebounceRef.current) {
      skipEmailDebounceRef.current = false;
      return;
    }
    const nextEmail = emailValue.trim() || undefined;
    if (nextEmail === (filters.email || undefined)) return;
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, email: nextEmail, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [emailValue, setFilters, filters.email]);

  useEffect(() => {
    if (skipPhoneDebounceRef.current) {
      skipPhoneDebounceRef.current = false;
      return;
    }
    const nextPhone = phoneValue.trim() || undefined;
    if (nextPhone === (filters.phone || undefined)) return;
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, phone: nextPhone, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [phoneValue, setFilters, filters.phone]);

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleTypeFilter = (type: 'deposit' | 'withdrawal' | undefined) => {
    setFilters(prev => ({ ...prev, type, page: 1 }));
  };

  const handleStatusFilter = useCallback((status: Transaction['status'] | undefined) => {
    setFilters(prev => ({ ...prev, status, page: 1 }));
  }, [setFilters]);

  const handleClearFilters = useCallback(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setFilters((prev) => ({
      page: 1,
      limit: prev.limit ?? 20,
      buyerScope: prev.buyerScope,
      search: undefined,
      email: undefined,
      phone: undefined,
      type: undefined,
      status: undefined,
      provider: undefined,
    }));
    setSearchValue('');
    setEmailValue('');
    setPhoneValue('');
    setMoreFiltersOpen(false);
  }, [setFilters]);

  const handleBuyerScopeChange = useCallback(
    (buyerScope: NonNullable<TransactionFilters['buyerScope']>) => {
      setFilters((prev) => {
        const next: TransactionFilters = { ...prev, buyerScope, page: 1 };
        if (buyerScope !== 'user') {
          next.email = undefined;
          next.phone = undefined;
        }
        return next;
      });
      if (buyerScope !== 'user') {
        setEmailValue('');
        setPhoneValue('');
      }
    },
    [setFilters]
  );

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleExportConfirm = useCallback(async () => {
    setIsExporting(true);
    try {
      const exportFilters: TransactionFilters = {
        ...filters,
        page: 1,
        limit: EXPORT_LIMIT,
        sortBy,
        sortOrder,
      };
      const res = await apiClient.getTransactions(exportFilters);
      const list = res?.data?.transactions ?? [];
      const rows = list.map((t: Transaction) => ({
        'Internal ID': t.id,
        'Transaction ID': t.transactionId,
        'User ID': t.userId,
        Type: t.type,
        Gateway: t.provider ? (PROVIDER_CONFIG[t.provider as keyof typeof PROVIDER_CONFIG]?.label ?? t.provider) : '',
        'Payment Method': t.paymentMethod,
        Amount: t.amount,
        Currency: t.currency,
        Status: t.fulfillmentStatus || t.status,
        'Fulfillment Status': t.fulfillmentStatus ?? '',
        'Payment Status': t.status,
        Description: t.description ?? '',
        'Created At': fmt(t.created_at),
      }));
      exportToExcel(rows, {
        sheetName: 'Payment transactions',
        fileName: `payment-transactions-export-${format(new Date(), 'yyyy-MM-dd')}`,
      });
      toast.success('Export completed successfully');
      setShowExportModal(false);
    } catch (err) {
      console.error('Export transactions error:', err);
      toast.error('Failed to export transactions. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [filters, sortBy, sortOrder, fmt]);

  const transactions = useMemo(
    () => transactionsData?.data?.transactions ?? [],
    [transactionsData],
  );

  const allocationParentIds = useMemo(
    () =>
      transactions
        .filter((transaction) => transaction.hasChildAllocations)
        .map((transaction) => transaction.id)
        .sort((a, b) => a - b),
    [transactions],
  );

  useEffect(() => {
    setExpandedParentIds(new Set());
    setAllocationsByParentId({});
    setLoadingAllocationIds(new Set());
    setFailedAllocationIds(new Set());
  }, [filters.page, filters.limit, filters.search, filters.buyerScope, filters.type, filters.status, filters.email, filters.phone, filters.provider, filters.sortBy, filters.sortOrder]);

  useEffect(() => {
    if (isLoading) return;

    const parentIds = allocationParentIds;

    if (parentIds.length === 0) return;

    const runId = ++preloadRunRef.current;

    const loadAllocations = async (parentId: number) => {
      setLoadingAllocationIds((prev) => new Set(prev).add(parentId));
      try {
        const response = await apiClient.getTransactionAllocations(parentId);
        if (preloadRunRef.current !== runId) return;
        const allocations = response.data?.allocations ?? [];
        setAllocationsByParentId((prev) => ({ ...prev, [parentId]: allocations }));
      } catch (err) {
        if (preloadRunRef.current !== runId) return;
        console.error('Preload transaction allocations error:', err);
        setFailedAllocationIds((prev) => new Set(prev).add(parentId));
      } finally {
        if (preloadRunRef.current !== runId) return;
        setLoadingAllocationIds((prev) => {
          const next = new Set(prev);
          next.delete(parentId);
          return next;
        });
      }
    };

    const preloadAllocations = async () => {
      for (let i = 0; i < parentIds.length; i += ALLOCATION_PRELOAD_CONCURRENCY) {
        if (preloadRunRef.current !== runId) return;
        const batch = parentIds.slice(i, i + ALLOCATION_PRELOAD_CONCURRENCY);
        await Promise.all(batch.map(loadAllocations));
      }
    };

    void preloadAllocations();
  }, [allocationParentIds, isLoading]);

  const handleToggleAllocations = useCallback((event: React.MouseEvent, transaction: Transaction) => {
    event.stopPropagation();
    const parentId = transaction.id;
    setExpandedParentIds((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) {
        next.delete(parentId);
      } else {
        next.add(parentId);
      }
      return next;
    });
  }, []);

  const pagination = useMemo(
    () => transactionsData?.data?.pagination || DEFAULT_PAGINATION,
    [transactionsData]
  );

  const selectClass =
    'h-9 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive">Unable to load transactions</p>
          <button
            type="button"
            onClick={handleRetry}
            className="mt-2 text-sm text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <CardTitle className="flex items-center">
            <CreditCard className="h-5 w-5 mr-2" />
            Payment transaction list ({pagination.total || 0})
          </CardTitle>
          <div className="flex gap-2 w-full sm:w-auto flex-wrap">
            <Button variant="outline" size="sm" onClick={handleClearFilters} className="flex-1 sm:flex-none">
              <Filter className="h-4 w-4 mr-2" />
              Clear
            </Button>
            {canExport && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowExportModal(true)}
                disabled={transactions.length === 0}
                className="flex-1 sm:flex-none"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Export</span>
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => refetch()} className="flex-1 sm:flex-none">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2 mb-3">
          {(
            [
              { scope: 'user' as const, label: 'Players', Icon: User },
              { scope: 'agent' as const, label: 'Agents', Icon: Building2 },
              { scope: 'shop' as const, label: 'Shops', Icon: Store },
              { scope: 'super_agent' as const, label: 'Super agent', Icon: ShieldCheck },
            ] as const
          ).map(({ scope, label, Icon }) => {
            const active = (filters.buyerScope || 'user') === scope;
            return (
              <button
                key={scope}
                type="button"
                onClick={() => handleBuyerScopeChange(scope)}
                className={cn(
                  'inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                  active
                    ? 'border-primary bg-primary/10 text-foreground shadow-sm'
                    : 'border-transparent bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {label}
              </button>
            );
          })}
        </div>

        {/* Primary filter row — compact */}
        <div className="flex flex-col gap-2 mb-2">
          <div className="flex flex-col lg:flex-row gap-2 lg:items-center">
            <div className="relative flex-1 min-w-[12rem]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Transaction ID, search…"
                value={searchValue}
                onChange={handleSearchChange}
                className="pl-10 pr-9 h-9"
                aria-label="Search transactions"
              />
              {searchValue ? (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2 lg:flex-nowrap lg:shrink-0">
              <select
                value={filters.type || ''}
                onChange={(e) => handleTypeFilter(e.target.value === '' ? undefined : e.target.value as 'deposit' | 'withdrawal')}
                className={selectClass}
                aria-label="Transaction type"
              >
                <option value="">All types</option>
                <option value="deposit">Deposit</option>
                <option value="withdrawal">Withdrawal</option>
              </select>
              <select
                value={filters.status || ''}
                onChange={(e) => handleStatusFilter(e.target.value === '' ? undefined : e.target.value as Transaction['status'])}
                className={selectClass}
                aria-label="Status"
              >
                <option value="">All status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
                <option value="expired">Expired</option>
              </select>
              <select
                value={filters.provider || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, provider: e.target.value || undefined, page: 1 }))}
                className={selectClass}
                aria-label="Gateway"
              >
                {PROVIDER_FILTER_OPTIONS.map(opt => (
                  <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {isPlayerTab ? (
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => setMoreFiltersOpen((o) => !o)}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
              >
                {moreFiltersOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                More filters (email, phone)
              </button>
            </div>
          ) : null}

          {isPlayerTab && moreFiltersOpen ? (
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  type="email"
                  placeholder="Filter by email"
                  value={emailValue}
                  onChange={(e) => setEmailValue(e.target.value)}
                  className="pl-10 pr-9 h-9"
                  aria-label="Filter by email"
                />
                {emailValue ? (
                  <button type="button" onClick={() => setEmailValue('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground" aria-label="Clear email">
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  type="tel"
                  placeholder="Filter by phone"
                  value={phoneValue}
                  onChange={(e) => setPhoneValue(e.target.value)}
                  className="pl-10 pr-9 h-9"
                  aria-label="Filter by phone"
                />
                {phoneValue ? (
                  <button type="button" onClick={() => setPhoneValue('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground" aria-label="Clear phone">
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-48">
            <LoadingSpinner />
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center text-muted-foreground py-12 px-4 space-y-2 max-w-lg mx-auto">
            <p>No payment transactions match your filters.</p>
            <p className="text-sm">
              Shop counter deposit/redeem rows use provider &quot;Shop counter&quot; when recorded. Older
              counter-only wallet movements (before this was mirrored) may only appear on the player wallet.
            </p>
          </div>
        ) : (
          <div className="rounded-md border overflow-hidden">
            <div className="overflow-x-auto">
              <table className={cn(ADMIN_TABLE_FIXED_CLASS, 'caption-bottom text-sm relative min-w-[72rem]')}>
                <AdminTableColgroup columns={PAYMENT_TRANSACTION_COLUMNS} />
                <TableHeader className="sticky top-0 z-10 border-b bg-muted/95 backdrop-blur supports-[backdrop-filter]:bg-muted/80 shadow-sm">
                  <TableRow className="border-b-0 hover:bg-transparent">
                    <TableHead
                      className={cn(
                        'h-9 px-2 py-2 text-xs font-semibold text-muted-foreground whitespace-nowrap',
                        adminCellClass(PAYMENT_TRANSACTION_COLUMNS[PT.id]),
                      )}
                      title="Internal ID (payment_transactions.id)"
                    >
                      ID
                    </TableHead>
                    <TableHead
                      className={cn(
                        'h-9 px-2 py-2 text-xs font-semibold text-muted-foreground',
                        adminCellClass(PAYMENT_TRANSACTION_COLUMNS[PT.buyer]),
                      )}
                    >
                      Buyer
                    </TableHead>
                    <SortableTableHead
                      sortable
                      sortKey="status"
                      label="Status"
                      activeKey={headerSort.activeKey}
                      direction={headerSort.direction}
                      onSort={tableSort.toggleSort}
                      className={cn(
                        'h-9 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground',
                        adminCellClass(PAYMENT_TRANSACTION_COLUMNS[PT.status]),
                      )}
                    />
                    <SortableTableHead
                      sortable
                      sortKey="amount"
                      label="USD amount"
                      align="right"
                      activeKey={headerSort.activeKey}
                      direction={headerSort.direction}
                      onSort={tableSort.toggleSort}
                      className={cn(
                        'h-9 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap',
                        adminCellClass(PAYMENT_TRANSACTION_COLUMNS[PT.usdAmount]),
                      )}
                    />
                    <SortableTableHead
                      sortable
                      sortKey="creditAmount"
                      label="Credit amount"
                      align="right"
                      activeKey={headerSort.activeKey}
                      direction={headerSort.direction}
                      onSort={tableSort.toggleSort}
                      className={cn(
                        'h-9 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap',
                        adminCellClass(PAYMENT_TRANSACTION_COLUMNS[PT.creditAmount]),
                      )}
                    />
                    <TableHead
                      className={cn(
                        'h-9 px-2 py-2 text-xs font-semibold text-muted-foreground whitespace-nowrap',
                        adminCellClass(PAYMENT_TRANSACTION_COLUMNS[PT.gateway]),
                      )}
                    >
                      Gateway
                    </TableHead>
                    <TableHead
                      className={cn(
                        'h-9 px-2 py-2 text-xs font-semibold text-muted-foreground whitespace-nowrap',
                        adminCellClass(PAYMENT_TRANSACTION_COLUMNS[PT.method]),
                      )}
                    >
                      Method
                    </TableHead>
                    <TableHead
                      className={cn(
                        'h-9 px-2 py-2 text-xs font-semibold text-muted-foreground',
                        adminCellClass(PAYMENT_TRANSACTION_COLUMNS[PT.txnId]),
                      )}
                    >
                      Txn ID
                    </TableHead>
                    <SortableTableHead
                      sortable
                      sortKey="created_at"
                      label="Created"
                      activeKey={headerSort.activeKey}
                      direction={headerSort.direction}
                      onSort={tableSort.toggleSort}
                      className={cn(
                        'h-9 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap',
                        adminCellClass(PAYMENT_TRANSACTION_COLUMNS[PT.created]),
                      )}
                    />
                    <TableHead className={cn('h-9 px-2 py-2', adminCellClass(PAYMENT_TRANSACTION_COLUMNS[PT.actions]))}>
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((transaction) => {
                    const hasAllocations = Boolean(transaction.hasChildAllocations);
                    const isExpanded = expandedParentIds.has(transaction.id);
                    const allocations = allocationsByParentId[transaction.id] ?? [];
                    return (
                      <React.Fragment key={transaction.id}>
                        <TransactionTableRow
                          transaction={transaction}
                          onViewTransaction={onViewTransaction}
                          hasAllocations={hasAllocations}
                          isExpanded={isExpanded}
                          isLoadingAllocations={loadingAllocationIds.has(transaction.id)}
                          onToggleExpand={(event) => handleToggleAllocations(event, transaction)}
                        />
                        {isExpanded && allocations.length > 0
                          ? allocations.map((allocation) => (
                              <TransactionAllocationRow
                                key={allocation.id}
                                allocation={allocation}
                                colSpan={TABLE_COL_COUNT}
                              />
                            ))
                          : null}
                        {isExpanded && !loadingAllocationIds.has(transaction.id) && allocations.length === 0 ? (
                          <TableRow className="bg-muted/20">
                            <TableCell colSpan={TABLE_COL_COUNT} className="px-2 py-2 text-xs text-muted-foreground pl-10">
                              {failedAllocationIds.has(transaction.id)
                                ? 'Could not load game allocations.'
                                : 'No game allocation rows found for this deposit.'}
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </table>
            </div>
          </div>
        )}

        {pagination.total > 0 ? (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 text-sm text-muted-foreground">
            <p>
              Showing {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Rows</span>
                <select
                  value={pagination.limit}
                  onChange={(e) => setFilters((prev) => ({ ...prev, limit: Number(e.target.value), page: 1 }))}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  aria-label="Rows per page"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              {pagination.pages > 1 ? (
                <NumberedPagination
                  currentPage={pagination.page}
                  totalPages={pagination.pages}
                  onPageChange={handlePageChange}
                  disabled={isLoading}
                  variant="admin"
                />
              ) : null}
            </div>
          </div>
        ) : null}
      </CardContent>

      <ExportConfirmModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onConfirm={handleExportConfirm}
        isExporting={isExporting}
        title="Export to Excel"
        message="Only the latest 1000 records will be exported. Do you want to continue?"
      />
    </Card>
  );
};

export { TransactionList };
